// The Assistant's questions, each with its SUGGESTED answer pre-filled.
//
// ONE component for both Assistant surfaces. Whenever the Assistant needs
// something from the person — a missing value, which of several records they
// meant, a detail for a draft, an intent parameter — it asks through its
// `ask_user` tool, and every question carries the answer it recommends. This
// renders them as a small form whose inputs already hold those suggestions:
// the person accepts with one click or changes only what is wrong.
//
// Questions with `options` (several matching records, a field's allowed
// values) render as a choice list with the suggestion selected, plus "Other…"
// for a value that is not listed.

import { useState } from 'react'
import type { CSSProperties } from 'react'

export interface AssistantQuestion {
  id: string
  question: string
  /** The Assistant's recommended answer — pre-filled. */
  suggested: string
  options?: string[]
  /** Why it suggests that (e.g. "closest match", "from your profile"). */
  why?: string
}

export interface QuestionsCardProps {
  intro?: string
  questions: AssistantQuestion[]
  /** Set once answered (restored from the transcript) — the card is then read-only. */
  answered?: Record<string, string>
  disabled?: boolean
  onSubmit: (answers: Record<string, string>) => void
}

const OTHER = '__other__'
const BORDER = '#e5e7eb'
const MUTED = '#6b7280'

const inputStyle: CSSProperties = {
  width: '100%', boxSizing: 'border-box', padding: '6px 8px', fontSize: 14,
  border: '1px solid #d1d5db', borderRadius: 6, fontFamily: 'inherit', background: '#fff',
}

/** The answers as the person's next chat message. */
export function formatAnswers(questions: AssistantQuestion[], answers: Record<string, string>): string {
  const lines = questions.map((q) => {
    const a = (answers[q.id] ?? '').trim()
    const same = a && a === q.suggested.trim()
    return `- ${q.question}: ${a || '(left blank)'}${same ? ' (your suggestion)' : ''}`
  })
  return `My answers:\n${lines.join('\n')}`
}

export function QuestionsCard({ intro, questions, answered, disabled, onSubmit }: QuestionsCardProps) {
  const [values, setValues] = useState<Record<string, string>>(
    () => answered ? { ...answered } : Object.fromEntries(questions.map((q) => [q.id, q.suggested || ''])))
  const [other, setOther] = useState<Record<string, boolean>>(() => {
    const out: Record<string, boolean> = {}
    for (const q of questions) {
      const v = answered?.[q.id] ?? q.suggested
      if (q.options?.length && v && !q.options.includes(v)) out[q.id] = true
    }
    return out
  })
  const done = !!answered
  const locked = done || !!disabled
  const set = (id: string, v: string) => setValues((vs) => ({ ...vs, [id]: v }))

  return (
    <div style={{ border: `1px solid ${BORDER}`, borderRadius: 12, overflow: 'hidden', background: '#fff', minWidth: 280 }}>
      {intro && (
        <div style={{ padding: '8px 12px', fontSize: 14, fontWeight: 600, background: '#f9fafb', borderBottom: `1px solid ${BORDER}` }}>
          {intro}
        </div>
      )}
      <div style={{ padding: 10, display: 'grid', gap: 10 }}>
        {questions.map((q) => {
          const v = values[q.id] ?? ''
          const hasOptions = !!q.options?.length
          return (
            <label key={q.id} style={{ display: 'grid', gap: 4, fontSize: 14 }}>
              <span style={{ color: '#111827' }}>{q.question}</span>
              {hasOptions && !other[q.id] ? (
                <select style={inputStyle} value={q.options!.includes(v) ? v : ''} disabled={locked}
                  onChange={(e) => {
                    if (e.target.value === OTHER) { setOther((o) => ({ ...o, [q.id]: true })); set(q.id, '') } else set(q.id, e.target.value)
                  }}>
                  {!q.options!.includes(v) && <option value="">Choose…</option>}
                  {q.options!.map((o) => (
                    <option key={o} value={o}>{o}{o === q.suggested ? ' (suggested)' : ''}</option>
                  ))}
                  <option value={OTHER}>Other…</option>
                </select>
              ) : (
                <input style={inputStyle} value={v} disabled={locked} placeholder={q.suggested || ''}
                  onChange={(e) => set(q.id, e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && !locked) { e.preventDefault(); onSubmit(values) } }} />
              )}
              {(q.why || (q.suggested && v === q.suggested)) && (
                <span style={{ fontSize: 13, color: MUTED }}>
                  {v === q.suggested ? 'Suggested' : `Suggested: ${q.suggested}`}{q.why ? ` — ${q.why}` : ''}
                </span>
              )}
            </label>
          )
        })}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: 10, borderTop: `1px solid ${BORDER}` }}>
        {done ? (
          <span style={{ fontSize: 13.5, color: '#16a34a' }}>Answered.</span>
        ) : (
          <button type="button" disabled={locked} onClick={() => onSubmit(values)} style={{
            padding: '8px 14px', fontSize: 14, fontWeight: 600, borderRadius: 8, border: 'none',
            background: '#2563eb', color: '#fff', cursor: locked ? 'default' : 'pointer', opacity: locked ? 0.6 : 1,
          }}>Send answers</button>
        )}
      </div>
    </div>
  )
}

export default QuestionsCard
